import React, { useState, useEffect } from "react";
import { Container, Row, Col, Spinner, Alert } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { apiService } from "./services/apiService";
import "./ChatScreen.css";

const ViewChatHistory = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyName, setSurveyName] = useState("");

  useEffect(() => {
    if (!userId) {
      setError("Invalid user ID");
      setIsLoading(false);
      return;
    }
    fetchChatHistory();
  }, [userId]);

  const fetchChatHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getChatHistory(userId);
      setChatHistory(response.payload);
      setSurveyName(response.payload.survey_name || "");
    } catch (error) {
      console.error("Error fetching chat history:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <div className="text-muted">Loading chat history...</div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <div className="chat-screen-container d-flex justify-content-center align-items-center min-vh-100">
        <div className="chat-container bg-white shadow rounded-4 overflow-hidden position-relative">
          {/* Watermark */}
          <div
            className="chat-watermark"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/assets/main_logo.png)`,
            }}
          ></div>

          {/* Header */}
          <div className="chat-header text-white p-3 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="chat-header-logo rounded-circle bg-white me-3 d-flex align-items-center justify-content-center overflow-hidden">
                <img src="/assets/ai_logo.png" alt="AI Logo" />
              </div>
              <div>
                <h5 className="mb-0 fw-bold">{surveyName}</h5>
              </div>
            </div>
          </div>

          {/* Error Message Container */}
          <div className="chat-messages-container chat-messages p-3 overflow-auto position-relative">
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <div className="d-flex align-items-start justify-content-center">
                  <div>
                    <div className="bot-message-bubble bg-danger text-white p-4 rounded-3 shadow-sm">
                      <h6 className="mb-3">⚠️ Error</h6>
                      <p className="mb-0">{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-screen-container d-flex justify-content-center align-items-center min-vh-100">
      <div className="chat-container bg-white shadow rounded-4 overflow-hidden position-relative">
        {/* Watermark */}
        <div
          className="chat-watermark"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/assets/main_logo.png)`,
          }}
        ></div>

        {/* Header */}
        <div className="chat-header text-white p-3 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="chat-header-logo rounded-circle bg-white me-3 d-flex align-items-center justify-content-center overflow-hidden">
              <img src="/assets/ai_logo.png" alt="AI Logo" />
            </div>
            <div>
              <h5 className="mb-0 fw-bold">{surveyName}</h5>
            </div>
          </div>
        </div>

        {/* Chat Messages Container */}
        <div className="chat-messages-container chat-messages p-3 overflow-auto position-relative">
          {chatHistory &&
          chatHistory.conversation &&
          chatHistory.conversation.length > 0 ? (
            <>
              {chatHistory.conversation.map((exchange, index) => (
                <div key={index}>
                  {/* Bot Message */}
                  <div className="mb-3">
                    <div className="d-flex align-items-start">
                      <div className="bot-avatar rounded-circle text-white me-2 d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden">
                        <img src="/assets/ai_logo.png" alt="AI Logo" />
                      </div>
                      <div>
                        <div className="bot-message-bubble bg-white p-3 rounded-3 shadow-sm">
                          <p className="bot-message-text mb-0 text-dark text-start">
                            {exchange.bot}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* User Message */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-end">
                      <div>
                        <div className="user-message-bubble text-white p-3 rounded-3 shadow-sm">
                          <p className="user-message-text mb-0">
                            {exchange.human}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Summary Display */}
              {chatHistory.summary && (
                <div className="mb-3">
                  <div className="d-flex align-items-start">
                    <div className="bot-avatar rounded-circle text-white me-2 d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden">
                      <img src="/assets/ai_logo.png" alt="AI Logo" />
                    </div>
                    <div className="w-100">
                      <div className="bot-message-bubble bg-success text-white p-3 rounded-3 shadow-sm">
                        <h6 className="mb-2">📋 Chat Summary</h6>
                        <p className="mb-0">{chatHistory.summary}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <div className="d-flex align-items-start justify-content-center">
                  <div className="bot-avatar rounded-circle text-white me-3 d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden">
                    <img src="/assets/ai_logo.png" alt="AI Logo" />
                  </div>
                  <div>
                    <div className="bot-message-bubble bg-white p-4 rounded-3 shadow-sm">
                      <h6 className="mb-3 text-dark">📄 No Chat History</h6>
                      <p className="bot-message-text mb-0 text-dark text-start">
                        No conversation history found for user{" "}
                        <strong>{userId}</strong>. This user hasn't completed
                        any surveys yet.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewChatHistory;
